import { RsiData, RsiFieldKey } from '../../Types';
import { filterNotEmpty, isArray } from '@beeline.com/bp-design-system';
import { CommonFieldDefinitionModel } from '@utmost.co/enterprise-models';
import { CommonFieldDefinitionData } from '../../utils/useImporterData';
import {
  isExtensionRegexp,
  NULL_FIELD_VALUE,
  supportedURNsForEntityExistsByReferenceId,
  supportedURNsForReferenceIdValidations
} from '../../utils/Importer.constants';
import { ValidationsFragment } from '../../validations/data/__generated__/ValidateImporterColumn.graphql';

export type UrnToValueMap = { [key: string]: string };
export type UrnToValuesMap = { [key: string]: string[] };

export type ReferenceIdValidationModel = DeeplyDefined<ValidationsFragment>;
export type ReferenceIdValidationLookup = { [referenceId: string]: ReferenceIdValidationModel };

export interface ValidationParams {
  /* Pass entity reference ID to verify that the import file contains the same reference ID */
  targetReferenceId?: string;
}

export function isFieldValueNull(fieldValue: string | undefined) {
  return fieldValue === NULL_FIELD_VALUE;
}

export function isNotNullUndefinedOrEmpty(
  value?: string | string[] | boolean | null | undefined
): value is string | string[] | boolean {
  if (isArray(value)) {
    return filterNotEmpty(value).length > 0;
  }
  return value !== null && value !== undefined && value?.toString() !== '' && !isFieldValueNull(value?.toString());
}

export function isNotNullUndefined(
  value?: string | string[] | boolean | null | undefined
): value is string | string[] | boolean {
  if (isArray(value)) {
    return filterNotEmpty(value).length > 0;
  }
  return value !== null && value !== undefined && !isFieldValueNull(value?.toString());
}

export function isValid(value?: string | string[] | boolean | null | undefined): value is string | string[] | boolean {
  if (isArray(value)) {
    return filterNotEmpty(value).length > 0;
  }
  return value !== null && value !== undefined && !isFieldValueNull(value?.toString());
}

export function isUrnKey(urn: string): boolean {
  return urn.startsWith('urn:');
}

export function isInvalidField(
  field: ValidationsFragment | null | undefined
): field is { urn: string; index?: number; result: { valid: false; message?: string } } {
  return !!field?.urn && field.result?.valid === false;
}

export function sanitizeRsiData(data: RsiData, options?: { allow?: 'empty' | 'all' }): Record<string, string> {
  const emptyValueCheck =
    options?.allow === 'empty' ? isNotNullUndefined : options?.allow === 'all' ? () => true : isNotNullUndefinedOrEmpty;
  return Object.entries(data).reduce((acc, [key, value]) => {
    if (isUrnKey(key) && emptyValueCheck(value) && !isArray(value)) {
      const sanitizedValue = options?.allow === 'all' && !value ? '' : `${value}`;
      return {
        ...acc,
        [key]: sanitizedValue
      };
    }
    return acc;
  }, {});
}

export function sanitizeRsiArrayData(data: RsiData, options?: { allow?: 'empty' | 'all' }): Record<string, string[]> {
  const emptyValueCheck =
    options?.allow === 'empty' ? isNotNullUndefined : options?.allow === 'all' ? () => true : isNotNullUndefinedOrEmpty;
  return Object.entries(data).reduce<Record<string, string[]>>((acc, [key, value]) => {
    if (isUrnKey(key) && isArray(value) && emptyValueCheck(value)) {
      return {
        ...acc,
        [key]: value
      };
    }
    return acc;
  }, {});
}

function isExtension(urn: string) {
  return isExtensionRegexp.test(urn);
}

function isCustomObjectDefinition(fieldDefinition: CommonFieldDefinitionData) {
  return fieldDefinition.type === 'OBJECT' && isExtension(fieldDefinition.urn ?? '');
}

export function doesFieldDefinitionSupportReferenceIdValidation(
  fieldDefinition: CommonFieldDefinitionData | undefined
) {
  return fieldDefinition
    ? supportedURNsForReferenceIdValidations.includes(fieldDefinition.urn ?? '') ||
        isCustomObjectDefinition(fieldDefinition!)
    : false;
}

export function doesFieldDefinitionSupportEntityExistsById(fieldDefinition: CommonFieldDefinitionData | undefined) {
  return fieldDefinition ? supportedURNsForEntityExistsByReferenceId.has(fieldDefinition.urn ?? '') : false;
}

export function extractValueColumnsForReferenceIdValidation(
  row: RsiData,
  fieldsForExistenceDetectionOrReferenceIdValidation: RsiFieldKey[]
) {
  const urnToValueMap = fieldsForExistenceDetectionOrReferenceIdValidation.reduce<UrnToValueMap>((acc, urn) => {
    const rawValue = row[urn];
    if (isArray(rawValue)) {
      return acc;
    }
    const value = row[urn]?.toString();
    const columnIsNotEmpty = !!value && !isFieldValueNull(value?.toString());
    if (urn && columnIsNotEmpty) {
      acc[urn] = value.toString();
    }
    return acc;
  }, {});

  const urnToValuesMap = fieldsForExistenceDetectionOrReferenceIdValidation.reduce<UrnToValuesMap>((acc, urn) => {
    const rawValue = row[urn];
    if (isArray(rawValue)) {
      const value = filterNotEmpty(rawValue);
      if (urn && value.length > 0) {
        acc[urn] = value;
      }
      return acc;
    }
    return acc;
  }, {});

  return {
    urnToValueMap,
    urnToValuesMap
  };
}

export const mapFieldDefinitionsByUrn = (
  fieldDefinitions: CommonFieldDefinitionModel[]
): Map<String, CommonFieldDefinitionData> =>
  new Map<string, CommonFieldDefinitionData>(
    fieldDefinitions.map((fieldDefinition) => [fieldDefinition.urn, fieldDefinition as CommonFieldDefinitionData])
  );

export function formReferenceIdValidationLookup(
  valueIndexPairs: { value: string; index: number }[],
  validations: ReferenceIdValidationModel[]
): ReferenceIdValidationLookup {
  return validations.reduce<ReferenceIdValidationLookup>((acc, validation) => {
    const referenceId = valueIndexPairs.find((el) => el.index === validation.index)?.value;
    if (referenceId) {
      acc[referenceId] = validation;
    }
    return acc;
  }, {});
}
